import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import { ProximityScope } from '../primitives/ProximityHighlight.js';
import styles from './NavCluster.module.css';

export type NavClusterItem = {
  to: string;
  label: string;
  icon?: ReactNode;
  count?: number;
};

export type NavClusterIconLinkProps = {
  to: string;
  /** Shown as `aria-label` + `title` (icon-only). */
  label: string;
  icon: ReactNode;
  LinkComponent: ElementType<{
    to: string;
    className?: string;
    children?: ReactNode;
    'aria-label'?: string;
    title?: string;
    'aria-current'?: 'page' | boolean;
  }>;
  active?: boolean;
  /** Optional count after icon, separated by a subtle rule (e.g. inbox total). */
  count?: number;
};

/** Icon-only nav control; matches `NavCluster` trigger styling (no chevron). */
export function NavClusterIconLink({ to, label, icon, LinkComponent, active = false, count }: NavClusterIconLinkProps) {
  const isActive = active;
  const hasCount = typeof count === 'number';
  const a11yLabel = hasCount ? `${label} — ${count}` : label;
  return (
    <LinkComponent
      to={to}
      className={cx(styles.trigger, styles.triggerIconOnly, hasCount && styles.triggerIconWithCount)}
      aria-label={a11yLabel}
      title={a11yLabel}
      data-active={isActive ? 'true' : 'false'}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className={styles.triggerIcon}>{icon}</span>
      {hasCount ? (
        <>
          <span className={styles.triggerSep} aria-hidden />
          <span className={styles.triggerCount}>{count}</span>
        </>
      ) : null}
    </LinkComponent>
  );
}

export type NavClusterProps = {
  label: string;
  triggerIcon: ReactNode;
  items: NavClusterItem[];
  /** e.g. `Link` from react-router-dom — must accept `to` + `className` + `children`. */
  LinkComponent: ElementType<{
    to: string;
    className?: string;
    children?: ReactNode;
    onClick?: () => void;
    role?: string;
    'data-wp-proximity-item'?: string;
    'data-active'?: string;
  }>;
  /** True when current route matches any `items[].to`. */
  active?: boolean;
  /** Per-item active predicate (e.g. pathname match). */
  isItemActive?: (to: string) => boolean;
  /** Hover tooltip on trigger (defaults to label + list of destinations). */
  menuTitle?: string;
};

const OPEN_MS = 80;
const CLOSE_MS = 280;
const PANEL_MIN_WIDTH = 220;
/** Keep flyout alive while pointer stays in trigger↔panel corridor (incl. over table rows below). */
const CORRIDOR_PAD_PX = 28;

export function NavCluster({
  label,
  triggerIcon,
  items,
  LinkComponent,
  active = false,
  isItemActive,
  menuTitle,
}: NavClusterProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const bridgeRef = useRef<HTMLDivElement | null>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; minWidth: number } | null>(
    null,
  );

  const clearTimers = useCallback(() => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
    openT.current = null;
    closeT.current = null;
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    openT.current = setTimeout(() => setOpen(true), OPEN_MS);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    if (pinned) return;
    clearTimers();
    closeT.current = setTimeout(() => setOpen(false), CLOSE_MS);
  }, [clearTimers, pinned]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const closeMenu = useCallback(() => {
    clearTimers();
    setPinned(false);
    setOpen(false);
  }, [clearTimers]);

  const holdOpen = useCallback(() => {
    clearTimers();
    setOpen(true);
  }, [clearTimers]);

  const togglePinnedMenu = useCallback(() => {
    clearTimers();
    if (open && pinned) {
      closeMenu();
      return;
    }
    setPinned(true);
    setOpen(true);
  }, [clearTimers, closeMenu, open, pinned]);

  const updatePortalPos = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const minWidth = Math.max(PANEL_MIN_WIDTH, r.width);
    let left = r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - minWidth - 8));
    const top = r.bottom + 6;
    setPortalPos({ top, left, minWidth });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPortalPos(null);
      return;
    }
    updatePortalPos();
  }, [open, updatePortalPos]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => updatePortalPos();
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, updatePortalPos]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      if (bridgeRef.current?.contains(t)) return;
      closeMenu();
    };
    document.addEventListener('pointerdown', onDocPointer, true);
    return () => document.removeEventListener('pointerdown', onDocPointer, true);
  }, [closeMenu, open]);

  useEffect(() => {
    if (!open) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isTopOverlay(stackId)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      closeMenu();
      triggerRef.current?.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      popOverlay(stackId);
    };
  }, [closeMenu, open]);

  /**
   * Hover corridor: while flyout open, pointer over trigger, bridge, panel, or the
   * padded rectangle spanning them keeps the menu alive — even when the hit target
   * is a data-table row painted under that corridor.
   */
  useEffect(() => {
    if (!open || pinned) return;

    const pointInRect = (x: number, y: number, r: DOMRect, pad: number) =>
      x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad;

    const inCorridor = (x: number, y: number) => {
      const root = rootRef.current;
      const panel = panelRef.current;
      if (!root || !panel) return false;

      const hit = document.elementFromPoint(x, y);
      if (hit instanceof Node) {
        if (root.contains(hit) || panel.contains(hit) || bridgeRef.current?.contains(hit)) {
          return true;
        }
      }

      const tr = root.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      if (pointInRect(x, y, tr, CORRIDOR_PAD_PX) || pointInRect(x, y, pr, CORRIDOR_PAD_PX)) {
        return true;
      }
      const left = Math.min(tr.left, pr.left) - CORRIDOR_PAD_PX;
      const right = Math.max(tr.right, pr.right) + CORRIDOR_PAD_PX;
      const top = Math.min(tr.top, pr.top) - CORRIDOR_PAD_PX;
      const bottom = Math.max(tr.bottom, pr.bottom) + CORRIDOR_PAD_PX;
      return x >= left && x <= right && y >= top && y <= bottom;
    };

    let wasInside = true;
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      const inside = inCorridor(e.clientX, e.clientY);
      if (inside) {
        wasInside = true;
        clearTimers();
        return;
      }
      if (wasInside) {
        wasInside = false;
        scheduleClose();
      }
    };

    document.addEventListener('pointermove', onPointerMove);
    return () => document.removeEventListener('pointermove', onPointerMove);
  }, [open, pinned, clearTimers, scheduleClose]);

  if (items.length === 0) return null;

  const defaultMenuTitle =
    menuTitle ?? `${label} — ${items.map((it) => it.label).join(' · ')}`;

  if (items.length === 1) {
    const only = items[0];
    const isActive = isItemActive?.(only.to) ?? active;
    return (
      <div className={styles.wrap}>
        <LinkComponent
          to={only.to}
          className={styles.trigger}
          data-active={isActive ? 'true' : 'false'}
          aria-current={isActive ? 'page' : undefined}
          title={defaultMenuTitle}
        >
          <span className={styles.triggerIcon}>{triggerIcon}</span>
          <span className={styles.triggerLabel}>{only.label}</span>
        </LinkComponent>
      </div>
    );
  }

  const panelStyle: CSSProperties | undefined = portalPos
    ? { top: portalPos.top, left: portalPos.left, minWidth: portalPos.minWidth }
    : undefined;

  const bridgeNode =
    open && portalPos && typeof document !== 'undefined' && rootRef.current
      ? (() => {
          const tr = rootRef.current.getBoundingClientRect();
          const top = tr.bottom;
          const height = Math.max(0, portalPos.top - top);
          if (height <= 0) return null;
          return createPortal(
            <div
              ref={bridgeRef}
              className={styles.panelBridge}
              style={{
                top,
                left: portalPos.left,
                width: portalPos.minWidth,
                height,
              }}
              aria-hidden
              onMouseEnter={holdOpen}
              onMouseLeave={scheduleClose}
            />,
            document.body,
          );
        })()
      : null;

  const panelNode =
    open && portalPos && typeof document !== 'undefined'
      ? createPortal(
          <ProximityScope
            axis="y"
            as="ul"
            ref={panelRef}
            className={cx(styles.panel, styles.panelPortal)}
            style={panelStyle}
            role="menu"
            onMouseEnter={holdOpen}
            onMouseLeave={scheduleClose}
          >
            {items.map((it) => {
              const itemActive = isItemActive?.(it.to) ?? false;
              return (
                <li key={it.to} role="none">
                  <LinkComponent
                    to={it.to}
                    className={styles.item}
                    data-wp-proximity-item=""
                    data-active={itemActive ? 'true' : 'false'}
                    role="menuitem"
                    onClick={closeMenu}
                  >
                    <span className={styles.itemIcon}>{it.icon}</span>
                    <span className={styles.itemLabel}>{it.label}</span>
                    {typeof it.count === 'number' ? (
                      <span className={styles.itemCount}>{it.count}</span>
                    ) : null}
                  </LinkComponent>
                </li>
              );
            })}
          </ProximityScope>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={cx(styles.wrap, open && styles.open)}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocusCapture={() => {
        if (!pinned) scheduleOpen();
      }}
      onBlurCapture={(e) => {
        if (pinned) return;
        const t = e.relatedTarget as Node | null;
        if (t && e.currentTarget.contains(t)) return;
        if (t && panelRef.current?.contains(t)) return;
        scheduleClose();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        data-active={active ? 'true' : 'false'}
        aria-expanded={open}
        aria-haspopup="menu"
        title={defaultMenuTitle}
        onClick={togglePinnedMenu}
      >
        <span className={styles.triggerIcon}>{triggerIcon}</span>
        <span className={styles.triggerLabel}>{label}</span>
        <IconChevronDown className={styles.chev} stroke={1.5} aria-hidden />
      </button>
      {bridgeNode}
      {panelNode}
    </div>
  );
}
